class ApiService {
  constructor() {
    this.HOST_PLATFORM = this.getRuntimeOrigin(
      "VUE_APP_PLATFORM_API_ORIGIN",
      this.isLocalRuntime()
        ? this.getLocalApiOrigin()
        : "https://www.yirenyian.com",
    );
    this.HOST_PLATFORM_FRONTEND_V3 = this.getRuntimeOrigin(
      "VUE_APP_PLATFORM_LOGIN_ORIGIN",
      "https://www.yirenyian.com",
    );
    this.LOGIN_PATHNAME = `${this.HOST_PLATFORM_FRONTEND_V3}/user/login`;
    this.GET_CURRENT_USERINFO_URL = `${this.HOST_PLATFORM}/api/user/getUserInfo`;
    this.USER_LOGOUT_URL = `${this.HOST_PLATFORM}/api/user/logout`;
    this.CONVERSATION_LIST_URL = `${this.HOST_PLATFORM}/api/big-model/conversation/list`;
    this.CONVERSATION_CREATE_URL = `${this.HOST_PLATFORM}/api/big-model/conversation/create`;
    this.CONVERSATION_REMOVE_URL = `${this.HOST_PLATFORM}/api/big-model/conversation/remove`;
    this.MESSAGE_LIST_URL = `${this.HOST_PLATFORM}/api/big-model/message/list`;
    this.UI_STATE_URL = `${this.HOST_PLATFORM}/api/big-model/message/ui-state`;
    this.MESSAGE_IMPORT_URL = `${this.HOST_PLATFORM}/api/big-model/message/import`;
    this.CHAT_STREAM_URL = `${this.HOST_PLATFORM}/api/big-model/chat/completions/stream`;
    this.LOGIN_COOKIE_KEY = "qiwiLoginCookieKey";
    this.REQUEST_HEADER_TOKEN_NAME = "accessKey";
    this.MODEL_ID = this.getRuntimeValue(
      "VUE_APP_BIG_MODEL_ID",
      "deepseek-flash",
    );
    this.CONVERSATION_NAME_PREFIX = "【特教求索】";
    this.CONVERSATION_STORAGE_KEY_PREFIX = "deepseek_tejiao123_conversation_id";
    this.controller = null;
    this.conversationId = "";
  }

  isLocalRuntime() {
    if (typeof window === "undefined") {
      return false;
    }
    return ["localhost", "127.0.0.1", "::1", "[::1]"].includes(
      `${window.location.hostname || ""}`.toLowerCase(),
    );
  }

  getRuntimeOrigin(envKey, fallback) {
    const envValue =
      typeof process !== "undefined"
        ? `${process.env?.[envKey] || ""}`.trim()
        : "";
    return (envValue || fallback || "").replace(/\/$/, "");
  }

  getLocalApiOrigin() {
    if (typeof window === "undefined") {
      return "http://127.0.0.1:5001";
    }
    const hostname = this.isLocalHostname(window.location.hostname)
      ? window.location.hostname
      : "127.0.0.1";
    return `http://${hostname || "127.0.0.1"}:5001`;
  }

  getRuntimeValue(envKey, fallback) {
    const envValue =
      typeof process !== "undefined"
        ? `${process.env?.[envKey] || ""}`.trim()
        : "";
    return envValue || fallback;
  }

  isLocalHostname(hostname = "") {
    return ["localhost", "127.0.0.1", "::1", "[::1]"].includes(
      `${hostname || ""}`.toLowerCase(),
    );
  }

  normalizeLocalUrlHost(rawUrl = "") {
    if (!rawUrl || typeof window === "undefined") {
      return rawUrl;
    }
    try {
      const url = new URL(rawUrl, window.location.href);
      return url.toString();
    } catch (error) {
      return rawUrl;
    }
  }

  getCookie(name) {
    if (typeof document === "undefined") {
      return "";
    }
    const target = `${name}=`;
    const item = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith(target));
    return item ? decodeURIComponent(item.slice(target.length)) : "";
  }

  getBaseHost(hostname = "") {
    if (!hostname || this.isLocalHostname(hostname)) {
      return "";
    }
    const parts = hostname.split(".").filter(Boolean);
    return parts.length >= 2 ? parts.slice(-2).join(".") : hostname;
  }

  setCookie(name, value) {
    if (typeof document === "undefined" || !value) {
      return;
    }
    const maxAge = 365 * 24 * 60 * 60;
    const domain =
      typeof window !== "undefined"
        ? this.getBaseHost(window.location.hostname || "")
        : "";
    const domainPart = domain ? `; domain=${domain}` : "";
    const securePart = this.isLocalRuntime() ? "" : "; secure";
    document.cookie = `${name}=${encodeURIComponent(
      value,
    )}; path=/; max-age=${maxAge}; samesite=lax${domainPart}${securePart}`;
  }

  removeCookie(name, domain = "") {
    if (typeof document === "undefined") {
      return;
    }
    const domainPart = domain ? `; domain=${domain}` : "";
    const securePart = this.isLocalRuntime() ? "" : "; secure";
    document.cookie = `${name}=; path=/; max-age=0; samesite=lax${domainPart}${securePart}`;
  }

  clearLoginCookie() {
    this.removeCookie(this.LOGIN_COOKIE_KEY);
    if (typeof window !== "undefined") {
      const domain = this.getBaseHost(window.location.hostname || "");
      if (domain) {
        this.removeCookie(this.LOGIN_COOKIE_KEY, domain);
      }
    }
  }

  consumeTokenFromUrl() {
    if (typeof window === "undefined") {
      return "";
    }

    const url = new URL(window.location.href);
    const queryToken = `${url.searchParams.get("token") || ""}`.trim();
    if (queryToken) {
      url.searchParams.delete("token");
      window.history.replaceState(
        {},
        "",
        `${url.pathname}${url.search}${url.hash}`,
      );
      this.setCookie(this.LOGIN_COOKIE_KEY, queryToken);
      return queryToken;
    }

    const hash = window.location.hash || "";
    const queryIndex = hash.indexOf("?");
    if (queryIndex < 0) {
      return "";
    }
    const hashPath = hash.slice(0, queryIndex);
    const hashQuery = hash.slice(queryIndex + 1);
    const params = new URLSearchParams(hashQuery);
    const hashToken = `${params.get("token") || ""}`.trim();
    if (!hashToken) {
      return "";
    }
    params.delete("token");
    const nextHashQuery = params.toString();
    const nextHash = nextHashQuery ? `${hashPath}?${nextHashQuery}` : hashPath;
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${window.location.search}${nextHash}`,
    );
    this.setCookie(this.LOGIN_COOKIE_KEY, hashToken);
    return hashToken;
  }

  getAccessKey() {
    const tokenFromUrl = this.consumeTokenFromUrl();
    return tokenFromUrl || this.getCookie(this.LOGIN_COOKIE_KEY);
  }

  hasLoginToken() {
    return !!this.getAccessKey();
  }

  jumpToLogin(targetUrl) {
    if (typeof window === "undefined") {
      return;
    }
    const rawTarget = targetUrl || window.location.href;
    const target = encodeURIComponent(this.normalizeLocalUrlHost(rawTarget));
    window.location.href = `${this.LOGIN_PATHNAME}?from=tejiao123&target=${target}`;
  }

  async logout() {
    try {
      if (this.getAccessKey()) {
        await this.postJson(this.USER_LOGOUT_URL, {}, { allowAuthError: true });
      }
    } catch (error) {
      // 后端退出失败不阻塞前端清理 cookie。
    }
    this.clearLoginCookie();
    this.resetConversation();
    this.jumpToLogin();
  }

  getTokenHash(token = "") {
    let hash = 0;
    for (let i = 0; i < token.length; i += 1) {
      hash = ((hash << 5) - hash + token.charCodeAt(i)) | 0;
    }
    return Math.abs(hash).toString(36);
  }

  getUserStorageSuffix() {
    const accessKey = this.getAccessKey();
    return accessKey ? this.getTokenHash(accessKey) : "anonymous";
  }

  getConversationStorageKey() {
    return `${
      this.CONVERSATION_STORAGE_KEY_PREFIX
    }_${this.getUserStorageSuffix()}`;
  }

  getStoredConversationId() {
    if (typeof localStorage === "undefined") {
      return "";
    }
    return localStorage.getItem(this.getConversationStorageKey()) || "";
  }

  setStoredConversationId(conversationId) {
    this.conversationId = conversationId || "";
    if (typeof localStorage === "undefined") {
      return;
    }
    const storageKey = this.getConversationStorageKey();
    if (conversationId) {
      localStorage.setItem(storageKey, conversationId);
    } else {
      localStorage.removeItem(storageKey);
    }
  }

  setActiveConversation(conversationId) {
    this.setStoredConversationId(conversationId || "");
  }

  resetConversation() {
    this.setStoredConversationId("");
  }

  buildAuthError(
    message = "登录状态已失效，请从七维特教AI伙伴重新登录后再使用特教求索。",
  ) {
    const error = new Error(message);
    error.isAuthError = true;
    return error;
  }

  async postJson(url, body = {}, options = {}) {
    const accessKey = this.getAccessKey();
    if (!accessKey) {
      throw this.buildAuthError();
    }

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        [this.REQUEST_HEADER_TOKEN_NAME]: accessKey,
      },
      credentials: "omit",
      body: JSON.stringify(body),
    });
    const text = await response.text();
    let json = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch (error) {
      // no-op
    }

    if (!response.ok) {
      const message =
        json?.msg || json?.message || text || `请求失败（${response.status}）`;
      if (
        response.status === 401 ||
        response.status === 403 ||
        `${message}`.includes("token")
      ) {
        throw this.buildAuthError(message);
      }
      throw new Error(message);
    }
    if (json && json.code && json.code !== 2000) {
      const error = new Error(json.msg || "请求失败");
      error.code = json.code;
      if (json.code === 4000 || `${json.msg || ""}`.includes("token")) {
        error.isAuthError = true;
        if (!options.allowAuthError) {
          throw error;
        }
      } else {
        throw error;
      }
    }

    return json?.data ?? json;
  }

  async getCurrentUser() {
    return this.postJson(this.GET_CURRENT_USERINFO_URL, {});
  }

  async listConversations(params = {}) {
    return this.postJson(this.CONVERSATION_LIST_URL, {
      pageNo: 1,
      pageSize: 50,
      type: "model",
      name: "特教求索",
      ...params,
    });
  }

  async listConversationMessages(conversationId) {
    if (!conversationId) {
      return { rows: [] };
    }
    return this.postJson(this.MESSAGE_LIST_URL, {
      conversationId,
      pageNo: 1,
      pageSize: 1000,
    });
  }

  async createConversation(name = `${this.CONVERSATION_NAME_PREFIX}新会话`) {
    const conversation = await this.postJson(this.CONVERSATION_CREATE_URL, {
      type: "model",
      name,
      modelId: this.MODEL_ID,
    });

    if (!conversation?._id) {
      throw new Error("创建后端会话失败");
    }

    this.setStoredConversationId(conversation._id);
    return conversation;
  }

  async removeConversation(conversationId) {
    if (!conversationId) {
      return;
    }
    await this.postJson(this.CONVERSATION_REMOVE_URL, {
      _id: conversationId,
    });
    if (this.conversationId === conversationId) {
      this.resetConversation();
    }
  }

  async saveUiState(messageId, state, submit = false) {
    return this.postJson(this.UI_STATE_URL, { messageId, state, submit });
  }

  async importMessages(conversationId, messages) {
    if (!conversationId || !Array.isArray(messages) || messages.length <= 0) {
      return { insertedCount: 0 };
    }
    return this.postJson(this.MESSAGE_IMPORT_URL, {
      conversationId,
      messages,
    });
  }

  async ensureConversation(conversationId = "") {
    if (conversationId) {
      this.setStoredConversationId(conversationId);
      return conversationId;
    }

    this.conversationId = this.conversationId || this.getStoredConversationId();
    if (this.conversationId) {
      return this.conversationId;
    }

    const conversation = await this.createConversation();
    return conversation._id;
  }

  buildBackendUserMessage(messages) {
    const userMessages = messages.filter((item) => item.role === "user");
    const currentUserMessage = userMessages[userMessages.length - 1];
    const content = currentUserMessage?.content || "";
    const question = `${currentUserMessage?.show_content || (Array.isArray(content)
      ? content.filter((item) => item.type === "text").map((item) => item.text).join("\n")
      : content)}`.trim();

    const fileContext = messages
      .filter(
        (item) =>
          item.role === "system" &&
          `${item.content || ""}`.includes("用户上传的文件内容"),
      )
      .map((item) => `${item.content || ""}`.trim())
      .filter(Boolean)
      .join("\n\n");

    return {
      role: "user",
      content: Array.isArray(content)
        ? content
        : fileContext ? `${fileContext}\n\n用户问题：${question}` : content,
      show_content: question,
    };
  }

  getThinkingConfig(options = {}) {
    if (this.MODEL_ID === "deepseek-flash") {
      return { type: "enabled" };
    }
    if (
      ["kimi-k2.6", "kimi-k2.5"].includes(this.MODEL_ID) &&
      options.webSearchEnabled
    ) {
      return { type: "disabled" };
    }
    if (["kimi-k2.6", "kimi-k2.5"].includes(this.MODEL_ID)) {
      return { type: "enabled" };
    }
    if (this.MODEL_ID === "doubao-seed-2-0-pro-260215") {
      return { type: "enabled" };
    }
    return null;
  }

  getReasoningEffort() {
    if (this.MODEL_ID === "doubao-seed-2-0-pro-260215") {
      return this.getRuntimeValue("VUE_APP_BIG_MODEL_REASONING_EFFORT", "low");
    }
    return "";
  }

  getMaxTokens() {
    const maxTokens = Number(
      this.getRuntimeValue("VUE_APP_BIG_MODEL_MAX_TOKENS", "32768"),
    );
    return Number.isFinite(maxTokens) && maxTokens > 0 ? maxTokens : 0;
  }

  async streamChat(messages, options = {}) {
    try {
      this.controller = new AbortController();
      const accessKey = this.getAccessKey();
      if (!accessKey) {
        throw this.buildAuthError();
      }

      const conversationId = await this.ensureConversation(
        options.conversationId || "",
      );
      const userMessage = this.buildBackendUserMessage(messages);
      const requestBody = {
        conversationId,
        model: this.MODEL_ID,
        stream: true,
        messages: [userMessage],
        tejiaoUi: 1,
        ...(options.uiAction ? { uiAction: { messageId: options.uiAction.messageId, state: options.uiAction.state } } : {}),
      };
      const thinkingConfig = this.getThinkingConfig(options);
      if (thinkingConfig) {
        requestBody.thinking = thinkingConfig;
      }
      const reasoningEffort = this.getReasoningEffort();
      if (reasoningEffort) {
        requestBody.reasoning_effort = reasoningEffort;
      }
      const maxTokens = this.getMaxTokens();
      if (maxTokens) {
        if (["kimi-k2.6", "kimi-k2.5"].includes(this.MODEL_ID)) {
          requestBody.max_completion_tokens = maxTokens;
        } else {
          requestBody.max_tokens = maxTokens;
        }
      }
      const forceWebSearch = !!options.webSearchEnabled;
      requestBody.webSearch = {
        enabled: forceWebSearch,
        mode: forceWebSearch ? "force" : "off",
      };

      const response = await fetch(this.CHAT_STREAM_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          [this.REQUEST_HEADER_TOKEN_NAME]: accessKey,
        },
        credentials: "omit",
        body: JSON.stringify(requestBody),
        signal: this.controller.signal,
      });

      const contentType = response.headers.get("Content-Type") || "";
      if (!response.ok || !response.body) {
        const errorText = await response.text().catch(() => "");
        let errorMsg = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMsg = errorJson?.msg || errorJson?.message || errorMsg;
        } catch (error) {
          // ignore parse error
        }
        if (
          response.status === 401 ||
          response.status === 403 ||
          `${errorMsg}`.includes("token")
        ) {
          throw this.buildAuthError(errorMsg || "登录状态已失效，请重新登录。");
        }
        throw new Error(errorMsg || `请求失败（${response.status}）`);
      }
      if (!contentType.includes("text/event-stream")) {
        const errorText = await response.text().catch(() => "");
        let errorMsg = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMsg = errorJson?.msg || errorJson?.message || errorMsg;
        } catch (error) {
          // ignore parse error
        }
        if (`${errorMsg}`.includes("token") || `${errorMsg}`.includes("登录")) {
          throw this.buildAuthError(errorMsg);
        }
        throw new Error(errorMsg || "请求失败");
      }

      const reader = response.body.getReader();

      return {
        conversationId,
        stream: reader,
        stop: () => {
          if (this.controller) {
            this.controller.abort();
            this.controller = null;
          }
        },
      };
    } catch (error) {
      console.error("API error:", error);
      throw error;
    }
  }
}

export const apiService = new ApiService();

export const logChat = async (question, response) => {
  try {
    const logData = {
      timestamp: new Date().toISOString(),
      question: question,
      response: response,
      user_agent: navigator.userAgent,
      platform: navigator.platform,
    };

    const result = await fetch(
      "https://sk.yirenyian.com/api/open/deepseeklog/create",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "omit",
        body: JSON.stringify(logData),
      },
    );

    if (!result.ok) {
      throw new Error(`HTTP error! status: ${result.status}`);
    }
  } catch (error) {
    console.error("Failed to log chat:", error);
  }
};
